<template>
  <div
    ref="appHero"
    v-editable="blok"
    class="app-hero"
    :class="animate && 'app-hero--animate'"
  >
    <div class="app-hero__content">
      <CustomRTE :blok="blok.text[0]" />
      <div
        v-if="
          (blok.ctas && blok.ctas.length > 0) ||
          (blok.zip_search && blok.zip_search.length === 1)
        "
        class="app-hero__ctas"
      >
        <CTAGroup
          v-if="
            blok.ctas &&
            blok.ctas.length > 0 &&
            (!blok.zip_search || blok.zip_search.length === 0)
          "
          :ctas="blok.ctas"
        />

        <RichTextEditor
          v-if="blok.legalText"
          class="mt-4"
          :blok="{ text: blok.legalText }"
        />
      </div>
      <PhoneAppDownload
        v-if="blok.phone_app_download"
        :class="{ hidePhoneAppMobile: blok.hidePhoneAppDownloadMobile }"
        :blok="blok.phone_app_download[0]"
      />
      <Picture
        v-if="blok.qr && blok.qr.filename"
        v-editable="blok"
        class="app-hero__qr drop-shadow-md"
        :blok="makePicture(blok.qr)"
        :width="177"
        loading="eager"
      />
    </div>
    <div class="app-hero__media">
      <Picture
        v-editable="blok"
        :blok="makeLayeredPicture()"
        :thumbnail="blok.image_layered"
        loading="eager"
        position="left"
      />
    </div>
  </div>
</template>

<script lang="ts" setup>
import PhoneAppDownload from './PhoneAppDownload.vue';
import { AppHeroSingleImageBlokProps, ImageBlokProps } from '@/types';

// Plugins
const { $noMotionPreference } = useNuxtApp();

// Props
const props = defineProps<{
  blok: AppHeroSingleImageBlokProps;
}>();

const makePicture = (src: ImageBlokProps) => ({
  image: src,
});

const makeLayeredPicture = () => ({
  image: props.blok.image_default,
});

// Variables & refs
let observer: IntersectionObserver;
const animate = ref(false);
const appHero = ref<HTMLDivElement>();

// Lifecycle : onMounted
onMounted(() => {
  // Only set up IO if no motion preference and there's an item to animate
  if ($noMotionPreference === true && appHero.value) {
    observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting === true) {
          animate.value = true;
        }
      });
    });

    observer.observe(appHero.value);
  }
});
</script>

<style lang="scss">
.app-hero {
  @include container;

  display: flex;
  flex-direction: column;
  gap: var(--spacing-l);
  justify-content: space-between;

  @media all and #{$mq-m-min} {
    flex-direction: row;
    gap: columns(1);
    height: 768px;
  }

  &__content {
    @media all and #{$mq-m-min} {
      margin-block-start: var(--spacing-s);
      max-width: columns(11);
    }

    .cta-group,
    .zip-search {
      margin-block-start: var(--spacing-s);
    }

    .cta {
      width: 100%;
    }

    .hidePhoneAppMobile {
      display: none;
      @media all and #{$mq-s-max} {
        display: none;
      }
    }
  }

  &__ctas {
    display: block;
    @media all and #{$mq-m-min} {
      max-width: columns(7);
    }
  }

  &__qr {
    margin-block-start: var(--spacing-s);
    display: none;
    @media all and #{$mq-m-min} {
      display: block;
    }
  }

  &__media {
    @media all and #{$mq-m-min} {
      margin-block-start: 2rem;
    }
  }

  .custom-rte__text {
    @media all and #{$mq-m-min} {
      max-width: columns(9);
    }
  }
}

@media screen and (prefers-reduced-motion: no-preference) {
  .app-hero {
    opacity: 0;
    padding-block-end: var(--spacing-m);
  }

  .app-hero--animate {
    --timing-text-cta-fade-delay: calc(
      var(--timing-heading-wipe) + var(--timing-hero-start-delay)
    );
    --timing-large-image-delay: calc(
      var(--timing-text-cta-fade-delay) + var(--timing-text-cta-fade)
    );
    --timing-small-image-delay: calc(
      var(--timing-large-image-delay) + var(--timing-large-image-fade)
    );

    opacity: 1;
    transition: opacity 0ms linear var(--timing-hero-start-delay);

    .eyebrow {
      animation: fade-in var(--timing-eyebrow) var(--easing-3) forwards;
      animation-delay: var(--timing-hero-start-delay);
      will-change: opacity;
    }

    .custom-heading {
      animation: wipe-left var(--timing-heading-wipe) var(--easing-2),
        scale var(--timing-heading-scale) var(--easing-2),
        fade-in var(--timing-heading-fade) var(--easing-3);
      animation-delay: var(--timing-hero-start-delay);
      animation-fill-mode: forwards;
      will-change: clip-path, transform, opacity;
    }

    .custom-rte__text,
    .app-hero__ctas {
      animation: fade-in var(--timing-text-cta-fade) var(--easing-3)
        var(--timing-text-cta-fade-delay) forwards;
      opacity: 0;
      will-change: opacity;
    }

    .picture {
      &__image {
        animation: wipe-right var(--timing-large-image-wipe) var(--easing-2)
          var(--timing-large-image-delay) forwards;
        display: block;
        will-change: clip-path;

        > img {
          animation: scale var(--timing-large-image-scale) var(--easing-2),
            fade-in var(--timing-large-image-fade) var(--easing-3);
          animation-delay: var(--timing-large-image-delay);
          animation-fill-mode: forwards;
          opacity: 0;
          transform: scale(1.1);
          will-change: transform, opacity, clip-path;
        }
      }

      &__thumbnail {
        animation: wipe-left var(--timing-small-image-wipe) var(--easing-2),
          fade-in var(--timing-small-image-fade) var(--easing-3);
        animation-delay: var(--timing-small-image-delay);
        animation-fill-mode: forwards;
        opacity: 0;
        will-change: clip-path, opacity;
      }
    }
  }
}

.offset-image {
  position: relative;
  top: 6rem;

  @media all and #{$mq-m-min} {
    position: relative;
    top: 4rem;
  }
  @media all and #{$mq-l-min} {
    position: relative;
    top: 12rem;
  }
}
</style>
